import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Pagination, Select } from '@mantine/core';
import {
    IconDots,
    IconRefresh,
    IconTrash,
    IconPencil,
    IconUserCheck,
    IconUserX,
    IconMailFast,
    IconLogin,
} from '@tabler/icons-react';
import { MutationFunctionOptions, useMutation } from '@apollo/client';
import { DataTable, DataTableColumn, DataTableProps } from 'mantine-datatable';

import { User } from '../../codegen/graphql';
import { TableActions, TableWrapper, ConfirmDeleteModal } from '../common';
import { DELETE_USER } from '../../apollo/mutations/user';
import {
    showErrorNotification,
    showLoadingNotification,
    updateFailedNotification,
    updateSuccessNotification,
} from '../../notifications';
import { useAuth } from '../../context/AuthProvider';
import stringifyNumber from '../../utility/stringifyNumber';
import { IMPERSONATE_USER, SEND_INVITE } from '../../apollo/mutations/auth';
import { GET_USERS_PAGINATED } from '../../apollo/queries/user';
import { useDataTableSorting } from '../../hooks/useDataTableSorting';
import { commonTableStyles } from '../../constants/tableStyles';

const ROLE_BADGE = {
    admin: { color: 'green', label: 'Admin' },
    user: { color: 'gray', label: 'User' },
    'store manager': { color: 'cyan', label: 'Store Manager' },
    'data strategist': { color: 'blue', label: 'Data Strategist' },
    'business leader': { color: 'violet', label: 'Business Leader' },
    'procurement manager': { color: 'orange', label: 'Procurement Manager' },
    'divisional manager': { color: 'red', label: 'Divisional Manager' },
    'ops executive': { color: 'yellow', label: 'Ops Executive' },
    'super admin': { color: 'green', label: 'Super Admin' },
    auditor: { color: 'grape', label: 'Auditor' },
    default: { color: 'gray', label: 'User' },
};

type Props = {
    users: User[];
    isLoading: boolean;
    totalCount: number;
    page: number;
    pageSize: number;
    search: string;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onSearchChange: (search: string) => void;
    onRefresh: () => void;
    updateUser: (options: MutationFunctionOptions) => Promise<void>;
    openEditUserSidesheet: (user: User) => void;
};

function UserTable({
    users,
    isLoading,
    totalCount,
    page,
    pageSize,
    search,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onRefresh,
    openEditUserSidesheet,
    updateUser,
}: Props) {
    const { authUser } = useAuth();

    const { sortStatus, handleSortStatusChange } = useDataTableSorting<User>();

    const [confirmDeleteModal, setConfirmDeleteModal] = useState<{
        isShown: boolean;
        user: User | null;
    }>({
        isShown: false,
        user: null,
    });

    const [deleteUser, { loading: deletingUser, error: deleteUserError }] =
        useMutation(DELETE_USER, {
            variables: {
                id: confirmDeleteModal?.user?.id,
            },
            optimisticResponse: {
                deleteUser: true,
            },
            refetchQueries: [GET_USERS_PAGINATED],
            awaitRefetchQueries: true,
        });

    const [sendInvite] = useMutation(SEND_INVITE);

    const [impersonate] = useMutation(IMPERSONATE_USER);

    const impersonateUser = useCallback(
        async (userId: string) => {
            try {
                const { data } = await impersonate({
                    variables: {
                        userToImpersonateId: userId,
                    },
                });
                const { accessToken, refreshToken } =
                    data?.impersonateUser || {};
                localStorage.setItem(
                    'superAdminAccessToken',
                    localStorage.getItem('accessToken') || '',
                );
                localStorage.setItem(
                    'superAdminRefreshToken',
                    localStorage.getItem('refreshToken') || '',
                );
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('refreshToken', refreshToken);
                window.location.reload();
            } catch (error) {
                showErrorNotification('Error logging in as user.');
            }
        },
        [impersonate],
    );

    const openConfirmDeleteModal = useCallback((user: User) => {
        setConfirmDeleteModal({ isShown: true, user });
    }, []);

    const closeConfirmDeleteModal = useCallback(() => {
        setConfirmDeleteModal({ isShown: false, user: null });
    }, []);

    const sendPortalInvite = useCallback(
        async (userId: string) => {
            try {
                showLoadingNotification(
                    'send-portal-invite',
                    'Sending portal invite',
                    'Please wait while we send the portal invite',
                );
                await sendInvite({
                    variables: {
                        userId,
                    },
                });
                updateSuccessNotification(
                    'send-portal-invite',
                    'Portal invite sent',
                    'Portal invite sent successfully',
                );
            } catch (error: any) {
                console.error('Failed to send portal invite:', error);
                updateFailedNotification(
                    'send-portal-invite',
                    'Failed to send portal invite',
                    'An error occurred while trying to send the portal invite. Please wait one minute and try again.',
                );
            }
        },
        [sendInvite],
    );

    const options = useCallback(
        (user: User) => {
            const myUserRole = authUser?.userRole?.role;

            const actions =
                myUserRole === 'Admin' || myUserRole === 'Super Admin'
                    ? [
                          {
                              label: 'Edit',
                              icon: (
                                  <IconPencil
                                      className="text-primary"
                                      size={20}
                                  />
                              ),
                              onClick: () => openEditUserSidesheet(user),
                              isShown: true,
                          },
                          {
                              label: 'Resend Invite',
                              icon: (
                                  <IconMailFast
                                      className="text-violet-500"
                                      size={20}
                                  />
                              ),
                              onClick: () => sendPortalInvite(user.id),
                              isShown: true,
                          },
                          {
                              label: user.isActive ? 'Deactivate' : 'Activate',
                              icon: user.isActive ? (
                                  <IconUserX
                                      size={20}
                                      className="text-orange-400"
                                  />
                              ) : (
                                  <IconUserCheck
                                      size={20}
                                      className="text-green-600"
                                  />
                              ),
                              onClick: () =>
                                  updateUser({
                                      variables: {
                                          input: {
                                              id: user.id,
                                              isActive: !user.isActive,
                                          },
                                      },
                                  }),
                              isShown: authUser?.id !== user.id,
                          },
                          {
                              label: 'Delete',
                              icon: (
                                  <IconTrash
                                      className="text-secondary"
                                      size={20}
                                  />
                              ),
                              onClick: () => openConfirmDeleteModal(user),
                              isShown: authUser?.id !== user.id,
                          },
                      ]
                    : [];

            if (myUserRole === 'Super Admin') {
                actions.push({
                    label: 'Log In as User',
                    icon: <IconLogin className="text-green-400" size={20} />,
                    onClick: () => impersonateUser(user.id),
                    isShown: true,
                });
            }

            return actions;
        },
        [
            authUser,
            updateUser,
            openConfirmDeleteModal,
            openEditUserSidesheet,
            sendPortalInvite,
            impersonateUser,
        ],
    );

    const renderActions = useCallback(
        (user: User) => {
            return (
                <div className="flex justify-center">
                    <TableActions
                        key={user.id}
                        options={options(user)}
                        row={user}
                    >
                        <Button
                            variant="subtle"
                            className="text-primary rounded-md p-1 w-min h-min"
                        >
                            <IconDots />
                        </Button>
                    </TableActions>
                </div>
            );
        },
        [options],
    );

    const renderRoles = useCallback((user: User) => {
        const { color, label } =
            ROLE_BADGE[
                user.userRole?.role.toLowerCase() as keyof typeof ROLE_BADGE
            ] || ROLE_BADGE.default;

        return (
            <Badge color={color} variant="light">
                {label}
            </Badge>
        );
    }, []);

    const renderContactNo = useCallback(
        (user: User) =>
            stringifyNumber(user.telNo) ||
            stringifyNumber(user.mobileNo) ||
            'N/A',
        [],
    );

    const columns = useMemo(
        (): DataTableColumn<User>[] => [
            {
                accessor: 'firstName',
                title: 'First Name',
                width: 150,
                textAlign: 'left',
                sortable: true,
            },
            {
                accessor: 'lastName',
                title: 'Last Name',
                width: 150,
                textAlign: 'left',
                sortable: true,
            },
            {
                accessor: 'username',
                title: 'Username',
                width: 150,
                textAlign: 'left',
                sortable: true,
            },
            {
                accessor: 'sapUser.sapId',
                title: 'SAP ID',
                width: 120,
                textAlign: 'left',
                render: ({ sapUser }) => sapUser?.sapId || 'N/A',
                sortable: true,
            },
            {
                accessor: 'contactNo',
                title: 'Contact No',
                width: 130,
                textAlign: 'left',
                render: renderContactNo,
                sortable: false,
            },
            {
                accessor: 'email',
                title: 'Email',
                width: 200,
                textAlign: 'left',
                sortable: true,
            },
            {
                accessor: 'role',
                title: 'Role',
                width: 150,
                textAlign: 'left',
                render: renderRoles,
                sortable: true,
            },
            {
                accessor: 'actions',
                title: 'Actions',
                width: 100,
                textAlign: 'left',
                render: renderActions,
                sortable: false,
            },
        ],
        [renderActions, renderRoles, renderContactNo],
    );

    const totalPages = Math.ceil(totalCount / pageSize);

    useEffect(() => {
        if (deleteUserError) {
            showErrorNotification('Error deleting user');
        }
    }, [deleteUserError]);

    return (
        <>
            <TableWrapper
                borderRadius="rounded-xl rounded-t-none"
                searchPlaceholder="Search Users"
                searchValue={search}
                onSearchChange={onSearchChange}
                buttons={
                    <Button
                        variant="subtle"
                        className="text-primary rounded-md p-1 w-min h-min "
                        onClick={onRefresh}
                    >
                        <IconRefresh />
                    </Button>
                }
                tableHeightOffset="15rem"
                pagination={
                    <div className="flex items-center gap-4 w-full justify-end">
                        <Select
                            data={['10', '20', '50', '100']}
                            value={pageSize.toString()}
                            onChange={value => {
                                if (value)
                                    onPageSizeChange(parseInt(value, 10));
                            }}
                            size="xs"
                            style={{ width: 80 }}
                        />
                        <Pagination
                            value={page}
                            total={totalPages || 1}
                            onChange={onPageChange}
                            size="sm"
                        />
                        <span className="text-xs text-gray-500 ml-2">
                            {totalCount > 0
                                ? `${pageSize * (page - 1) + 1}-${Math.min(
                                      pageSize * page,
                                      totalCount,
                                  )} of ${totalCount}`
                                : '0 results'}
                        </span>
                    </div>
                }
            >
                <DataTable
                    {...({
                        withTableBorder: true,
                        striped: true,
                        verticalSpacing: 'xs',
                        fz: 'sm',
                        noRecordsText: 'No records to display',
                        withColumnBorders: false,
                        highlightOnHover: true,
                        records: users,
                        columns,
                        fetching: isLoading,
                        idAccessor: 'id',
                        onSortStatusChange: handleSortStatusChange,
                        classNames: {
                            table: 'selection-column-fix',
                        },
                        styles: commonTableStyles,
                        ...(sortStatus && { sortStatus }),
                    } as DataTableProps<User>)}
                />
            </TableWrapper>
            <ConfirmDeleteModal
                onConfirm={deleteUser}
                opened={confirmDeleteModal.isShown}
                close={closeConfirmDeleteModal}
                subject="User"
                message={`Are You sure you want to delete
                        ${confirmDeleteModal?.user?.firstName} ${confirmDeleteModal?.user?.lastName}?`}
                loading={deletingUser}
            />
        </>
    );
}

export default UserTable;
